import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { scrollToSection } from "@/lib/api";

const LINES = [
  { text: "Your bridge to a" },
  { text: "world-class", accent: true },
  { text: "education." },
];

const COUNTRIES = ["UK", "USA", "Canada", "Australia", "Germany", "New Zealand"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const lineVariant = {
    hidden: { y: "115%" },
    show: (i) => ({
      y: "0%",
      transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.25 + i * 0.13 },
    }),
  };

  const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay },
  });

  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="relative overflow-hidden pt-[72px]">
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-40 right-[-12%] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,rgba(74,222,128,0.16),transparent_65%)]" />
        <div className="absolute bottom-[-25%] left-[-10%] h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(93,70,230,0.12),transparent_65%)]" />
      </motion.div>
      <div
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.028)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.028)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_80%_60%_at_50%_0%,black,transparent)]"
        aria-hidden="true"
      />

      <div className="container-x relative grid grid-cols-1 items-center gap-14 pb-20 pt-14 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.p {...fadeUp(0.1)} data-testid="hero-consultancy-label" className="overline-label flex items-center gap-3">
            <span className="h-px w-10 bg-mint" />
            Study Abroad Consultancy
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display text-[2.9rem] font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.1rem]"
          >
            {LINES.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1.5 -mb-1.5">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="show"
                  className={`block ${l.accent ? "mint-gradient-text pr-3 font-normal italic" : ""}`}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p {...fadeUp(0.7)} className="mt-7 max-w-xl text-base leading-relaxed text-mist sm:text-lg">
            From shortlisting universities to visas, loans and your first week on campus —
            Edubridge walks every step of the journey with you.
          </motion.p>

          <motion.div {...fadeUp(0.85)} className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollToSection("#book")}
              data-testid="hero-book-free-counseling-btn"
              className="group flex items-center gap-2 rounded-full bg-mint px-7 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-mintlight hover:shadow-[0_0_44px_rgba(74,222,128,0.4)]"
            >
              Book Free Counseling
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => scrollToSection("#destinations")}
              data-testid="hero-explore-destinations-btn"
              className="group flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-frost transition-all duration-300 hover:border-mint/50 hover:text-mint"
            >
              Explore Destinations
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div {...fadeUp(1)} className="mt-10 flex flex-wrap gap-2">
            {COUNTRIES.map((c) => (
              <span
                key={c}
                className="rounded-full border border-white/10 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-mist transition-colors duration-300 hover:border-mint/40 hover:text-mint"
              >
                {c}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(10% 10% 10% 10% round 36px)", opacity: 0 }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 36px)", opacity: 1 }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
            className="relative"
          >
            <motion.div
              style={{ y: imgY }}
              className="relative overflow-hidden rounded-[36px] border border-white/10"
              data-testid="hero-image-frame"
            >
              <img
                src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Iconic study-abroad destination city"
                className="h-[420px] w-full scale-110 object-cover sm:h-[540px]"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-ink/25" />
              <div className="glass-card absolute inset-x-5 bottom-5 rounded-3xl p-5" data-testid="hero-glass-card">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mint">
                  Free 30-minute session
                </p>
                <p className="mt-2 font-display text-lg leading-snug sm:text-xl">
                  Online, or at our office — your first step starts here.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="glass-card absolute -left-3 top-8 flex items-center gap-3 rounded-full py-2.5 pl-4 pr-5 sm:-left-8"
              data-testid="hero-floating-chip"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute h-full w-full animate-ping rounded-full bg-mint opacity-75" />
                <span className="h-2.5 w-2.5 rounded-full bg-mint" />
              </span>
              <span className="whitespace-nowrap text-xs font-semibold text-frost">
                Serving students worldwide
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        {...fadeUp(1.3)}
        onClick={() => scrollToSection("#services")}
        data-testid="hero-scroll-cue"
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-3 lg:flex"
        aria-label="Scroll to services"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-mist">Scroll</span>
        <span className="h-10 w-px bg-gradient-to-b from-mint to-transparent" />
      </motion.button>
    </section>
  );
}
